"use client";

import { useEffect } from "react";
import {
  DATA_EVENT_TRACKER_ATTR,
  defaultLogger,
  validateTracker,
  type TTrackerLogger,
  logGAEvent,
} from "@trex-2b/libs";

export type TDomEventTrackerOptions = {
  attributeName?: string;
  enabledEvents?: Array<"click" | "scroll" | "submit">;
  logger?: TTrackerLogger;
};

export function useDomEventDataTracker(
  options: TDomEventTrackerOptions = {},
): void {
  const {
    attributeName = DATA_EVENT_TRACKER_ATTR,
    enabledEvents = ["click"],
    logger = defaultLogger,
  } = options;

  useEffect(() => {
    if (typeof document === "undefined") return;

    const mapToParams = (params?: Record<string, unknown>) => ({
      ...(params ?? {}),
    });

    const sendClickAnalytics = (
      _name: string,
      _params: Record<string, unknown>,
    ) => {
      void _name;
      void _params;
      logGAEvent(_name, _params);
    };

    const handleClick = (evt: MouseEvent) => {
      try {
        const el = (evt.target as Element | null)?.closest?.(
          `[${attributeName}]`,
        ) as Element | null;
        if (!el) return;
        if (el.tagName === "FORM") return;
        const raw = el.getAttribute(attributeName);
        let trigger = (el as HTMLElement)?.innerText ?? "";
        if (!trigger) {
          const imgElement = el.querySelector("img");
          if (imgElement) {
            trigger = imgElement.alt || "";
          }
        }
        const v = validateTracker(raw ?? undefined);
        if (!v.ok || !v.value) {
          return;
        }
        const { params } = v.value ?? {};
        trigger = trigger.split("\n")[0].trim();
        const gaParams = mapToParams({ trigger, ...params });
        sendClickAnalytics("click", gaParams);
      } catch (e) {
        logger.error("[tracker] click handler failed", e);
      }
    };

    const handleSubmit = (evt: Event) => {
      try {
        const target = evt.target as Element | null;
        if (!target || target.tagName !== "FORM") return;
        const form = target as HTMLFormElement;
        const raw = form.getAttribute(attributeName);
        if (!raw) return;

        const v = validateTracker(raw);
        if (!v.ok || !v.value) return;

        const { params } = v.value as { params?: Record<string, unknown> };
        const gaParams = mapToParams(params);
        sendClickAnalytics("submit", gaParams);
      } catch (e) {
        logger.error("[tracker] submit handler failed", e);
      }
    };

    const handleScroll = () => {
      // 预留扩展点
    };

    if (enabledEvents.includes("click")) {
      document.addEventListener("click", handleClick, { capture: true });
    }
    if (enabledEvents.includes("submit")) {
      document.addEventListener("submit", handleSubmit as EventListener, {
        capture: true,
      });
    }
    if (enabledEvents.includes("scroll")) {
      window.addEventListener("scroll", handleScroll, { passive: true });
    }

    return () => {
      if (enabledEvents.includes("click")) {
        document.removeEventListener("click", handleClick, {
          capture: true,
        } as AddEventListenerOptions);
      }
      if (enabledEvents.includes("submit")) {
        document.removeEventListener(
          "submit",
          handleSubmit as EventListener,
          { capture: true } as AddEventListenerOptions,
        );
      }
      if (enabledEvents.includes("scroll")) {
        window.removeEventListener("scroll", handleScroll);
      }
    };
  }, [attributeName, enabledEvents, logger]);
}
